import type { JSX } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { produce } from 'immer';
import { z } from 'zod';

import type { Props as TextAreaProps } from '../text-area';
import type { Props as TextInputProps } from '../text-input';
import type { WithSlotProps } from '~/components/hocs';
import type { DefineSlots } from '~/components/layout/utils';
import type { Comment } from '~/types/comment';
import { zExposedPostCommentData } from '~/api/zod.gen';
import { analytics } from '~/components/common/google-analytics';
import { withSlot } from '~/components/hocs';
import { componentRegistry, componentSlots, renderChild } from '~/components/layout/utils';
import { SlotsProvider } from '~/components/providers';
import { useTemplate } from '~/hooks';
import { usePostComment } from '~/hooks/api/use-post-comment';
import { addComment, selectCommentThread, selectEmbedParams, useStore } from '~/store';
import { cn } from '~/utils/cn';
import { generateTempCommentId } from '~/utils/comment-id';
import { pxToRem } from '~/utils/rem';

export const NAME = 'CommentForm';

export const Slots = componentSlots(NAME, {
  FORM: 'form',
  NAME_INPUT: 'name',
  CONTENT_INPUT: 'content',
  SUBMIT_BTN: 'submitBtn',
});

type SlotProps = DefineSlots<
  typeof Slots,
  {
    [Slots.FORM]: JSX.IntrinsicElements['form'];
    [Slots.NAME_INPUT]: Partial<TextInputProps>;
    [Slots.CONTENT_INPUT]: Partial<TextAreaProps>;
    [Slots.SUBMIT_BTN]: JSX.IntrinsicElements['button'];
  }
>;

export interface Props extends React.PropsWithChildren, WithSlotProps {
  className?: string;
  parentId?: string;
  revealable?: boolean;
  containerRef?: React.Ref<HTMLFormElement>;
  onSubmit?: (comment: Comment) => void;
}

interface FormFieldState {
  value: string;
  errors: string[];
  touched: boolean;
}

interface FormState {
  isSubmitted: boolean;
  fields: {
    name: FormFieldState;
    content: FormFieldState;
  };
}

const CommentFormComponent = (props: Props) => {
  const { parentId, revealable = false, containerRef, onSubmit } = props;
  const [isRevealed, setIsRevealed] = useState(false);
  const template = useTemplate(NAME);
  const postComment = usePostComment();
  const parentCommentThread = useStore(state => selectCommentThread(state, parentId ?? ''));
  const { vcmId, userId, canonicalUrl, friendlyUrl } = useStore(selectEmbedParams);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const className = cn(props.className, isRevealed && 'revealed');

  const validateParams = useCallback(
    (data: { [K in keyof FormState['fields']]: string }) => {
      const result = zExposedPostCommentData.safeParse(
        {
          body: {
            responder: {
              id: userId,
              name: data.name,
            },
            origin: {
              id: vcmId,
              url: canonicalUrl.includes('mako.co.il') ? canonicalUrl : friendlyUrl,
              type: 'mako_article',
            },
            content: data.content,
            parentId,
          },
        },
        {
          error: iss => {
            switch (iss.code) {
              case 'too_small':
                if (iss.path?.at(-1) === 'name') return 'יש להזין שם מלא';
                if (iss.path?.at(-1) === 'content') return 'איך נתקדם בלי התגובה שלך?';
                return 'קלט לא חוקי';

              case 'too_big':
                return 'ארוך מדי';
            }
          },
        }
      );

      return result;
    },
    [parentId, userId, vcmId, canonicalUrl, friendlyUrl]
  );

  const getFormErrors = useCallback(
    (
      result: z.ZodSafeParseResult<z.infer<typeof zExposedPostCommentData>>
    ): { [K in keyof FormState['fields']]: string[] } => {
      if (result.success) {
        return { name: [], content: [] };
      }

      const errors = z.treeifyError(result.error);

      return {
        name: errors.properties?.body?.properties?.responder?.properties?.name?.errors ?? [],
        content: errors.properties?.body?.properties?.content?.errors ?? [],
      };
    },
    []
  );

  const initialFormState = useMemo(() => {
    const name = '';
    const content = '';
    const errors = getFormErrors(validateParams({ name, content }));

    return {
      isSubmitted: false,
      fields: {
        name: {
          value: name,
          errors: errors.name,
          touched: false,
        },
        content: {
          value: content,
          errors: errors.content,
          touched: false,
        },
      },
    };
  }, [validateParams, getFormErrors]);

  const [formState, setFormState] = useState<FormState>(() => initialFormState);

  const slots = useMemo<SlotProps>(
    () => ({
      [Slots.FORM]: {
        className,
        ref: containerRef,
        onSubmit: (e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();

          const form = e.currentTarget;
          const formData = new FormData(form);

          const data = Array.from(formData.entries()).reduce<Record<string, string>>((acc, [key, value]) => {
            if (typeof value === 'string') acc[key] = value;
            return acc;
          }, {});

          const validationResult = validateParams(data as { [K in keyof FormState['fields']]: string });

          if (validationResult.success) {
            analytics.gtag.event('comment_submit', { VCMID: vcmId });
            postComment.mutate(validationResult.data, {
              onSuccess: () => {
                const date = new Date().toISOString();
                const id = generateTempCommentId();
                const comment: Comment = {
                  id,
                  content: validationResult.data.body.content,
                  parent_id: validationResult.data.body.parentId ?? null,
                  responder_name: validationResult.data.body.responder.name,
                  depth: parentCommentThread?.parent ? parentCommentThread.parent.depth + 1 : 0,
                  root_created_at: parentCommentThread?.root_created_at ?? date,
                  root_id: parentCommentThread?.root_id ?? id,
                  created_at: date,
                  replies_count: 0,
                  status: 'pending',
                };

                // eslint-disable-next-line react-dom/no-flush-sync
                flushSync(() => {
                  setFormState(initialFormState);
                  onSubmit?.(comment);
                  addComment(comment);
                });

                if (revealable) {
                  if (textareaRef.current) {
                    textareaRef.current.style.height = pxToRem(35);
                  }
                  setIsRevealed(false);
                }

                const commentEl = document.querySelector(`[data-id="${comment.id}"]`);
                commentEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              },
            });

            return;
          } else {
            const errors = getFormErrors(validationResult);

            setFormState(
              produce(draft => {
                draft.fields.name.touched = true;
                draft.fields.name.errors = errors.name;
                draft.fields.content.touched = true;
                draft.fields.content.errors = errors.content;
              })
            );
          }

          setFormState(
            produce(draft => {
              draft.isSubmitted = true;
            })
          );
        },
      },
      [Slots.NAME_INPUT]: {
        ...formState.fields.name,
        errors: formState.fields.name.touched ? formState.fields.name.errors : [],
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.value.trim().length > 22) return;
          setFormState(
            produce(draft => {
              draft.fields.name.value = e.target.value;
              draft.fields.name.touched = true;
              draft.fields.name.errors = getFormErrors(
                validateParams({
                  name: e.target.value,
                  content: formState.fields.content.value,
                })
              ).name;
            })
          );
        },
        onBlur: () => {
          setFormState(
            produce(draft => {
              draft.fields.name.touched = true;
            })
          );
        },
      },
      [Slots.CONTENT_INPUT]: {
        ...formState.fields.content,
        errors: formState.fields.content.touched ? formState.fields.content.errors : [],
        textareaRef,
        onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          setFormState(
            produce(draft => {
              draft.fields.content.value = e.target.value;
              draft.fields.content.touched = true;
              draft.fields.content.errors = getFormErrors(
                validateParams({
                  name: formState.fields.name.value,
                  content: e.target.value,
                })
              ).content;
            })
          );
        },
        onFocus: () => {
          if (revealable) {
            analytics.gtag.event('comment_field_focus', { VCMID: vcmId });
            setIsRevealed(true);
          }
        },
        onBlur: () => {
          setFormState(
            produce(draft => {
              draft.fields.content.touched = true;
            })
          );
        },
      },
      [Slots.SUBMIT_BTN]: {
        className: cn(
          (postComment.isPending || [...formState.fields.content.errors, ...formState.fields.name.errors].length) &&
            'disabled'
        ),
      },
    }),
    [
      className,
      postComment,
      initialFormState,
      formState,
      parentCommentThread,
      revealable,
      textareaRef,
      vcmId,
      containerRef,
      validateParams,
      getFormErrors,
      onSubmit,
    ]
  );

  return <SlotsProvider slots={slots}>{props.children ?? renderChild(template)}</SlotsProvider>;
};

CommentFormComponent.displayName = NAME;

export const CommentForm = withSlot(CommentFormComponent);
componentRegistry.add(NAME, CommentForm as React.FC);
